import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TitleBar from "../../common/title-bar/title-bar";
import AdminActions from "../../common/admin-actions";
import InsuranceStatusPanel from "../../common/insurance-status-panel";
import TwoCheckIcon from "../../icons/two-check-icon";
import Button from "../../common/button/button";
import VerticalDashIcon from "../../icons/vertical-dash-icon";
import DashIcon from "../../icons/dash-icon";
import CalenderIcon from "../../icons/calender-icon";
import DetailsContainer from "../../common/details-container";
import AccountsIcon from "../../icons/accounts-icon";
import DistributorIcon from "../../icons/distributor-icon";
import ManufacturerIcon from "../../icons/manufacturer-icon";
import Text from "../../common/typography/text";
import Table from "../../common/table";
import TableOptionsCellMenu from "../../common/table/table-options-cell/table-options-cell-menu";
import moment from "moment";
import StatusIndicator from "../../common/status-indicator";
import ModalView from "../../common/modal";
import DropdownComponent from "../../common/dropdown-input";
import TextInput from "../../common/text-input";
import FileUpload from "../UploadFileNewComponent/UploadFile";
import InsuranceStatusSelector from "../../common/insurance-status-selector";
import { Checkbox, message, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import TextAreaComponent from "../../common/text-area";
import APIService from "../../Service/APIService.js";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import QuickOrder from "./QuickOrder";
import { createIVRInfoPdfBlob } from "./IVRInfoPdf";
import styles from "./IVRProduct.module.css";
import { useAuth } from "../AuthProvider";
import Swal from "sweetalert2";
import FailureIcon from "../../icons/failure-icon";
import {
  canDeleteIvrProduct,
  canEditIvrProduct,
} from "./ivrProductAccess.js";

const ivrProductColDefs = [
  {
    headerName: "PO Number",
    field: "PONumber",
    filter: true,
  },
  {
    headerName: "Order Date",
    field: "OrderDate",
    filter: true,
    cellRenderer: (params) =>
      moment(params.data.OrderDate).format("MM-DD-YYYY"),
  },

  {
    headerName: "Order Total",
    field: "OrderTotal",
    filter: true,
    cellDataType: "number",
    valueGetter: (params) =>
      parseFloat(String(params.data.Price).replace(/[^0-9.-]+/g, "")),
    cellRenderer: (params) => {
      const value = parseFloat(
        String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")
      );
      return `$${value.toLocaleString()}`;
    },
  },
  {
    headerName: "Status",
    field: "",
    filter: true,
    cellRenderer: (params) => <StatusIndicator params={params} />,
  },
  {
    headerName: "Options",
    field: "OrderTotal",
    filter: true,
    cellRenderer: (params) => (
      <TableOptionsCellMenu
        actionOption="Actions"
        actionOptionType="primary"
        optionOne="Track Order"
        optionOneType="primary"
        optionTwo="Add Details"
        optionTwoType="primary"
        optionThree="View Invoice"
        optionThreeType="warning"
        optionFour="Delete"
        optionFourType="failure"
      />
    ),
  },
];

const IVRProduct = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    PatientID,
    // CaseID,
    // InsuranceStatus,
    // InsuranceStatusText,
    // ProductName,
    AssociatedOrders,
    ApplicationDate,
    ApplicationNumber,
    ivrProductData: locationIvrProductData,
  } = location.state || {};

  const [ivrProduct, setIvrProduct] = useState(locationIvrProductData || null);
  const ivrProductData = ivrProduct || locationIvrProductData || null;

  const [openUploadInfo, setOpenUploadInfo] = useState(false);
  const [uploadedInfoFiles, setUploadedInfoFiles] = useState([]);
  const [isUploadingInfo, setIsUploadingInfo] = useState(false);
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);
  const [isUpdateSubmitting, setIsUpdateSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState({
    key: "pending",
    text: "Pending",
  });
  const [longTextValue, setLongTextValue] = useState(ivrProductData?.Note || "");
  const [productCaseID, setProductCaseID] = useState(ivrProductData?.CaseID || "");
  const [infoFields, setInfoFields] = useState([
    {
      FieldType: {
        label: "Text Field",
        value: "text-field",
      },
      FieldName: "",
    },
  ]);
  const [uploadInfoFields, setUploadInfoFields] = useState([]);

  const [openQuickOrder, setOpenQuickOrder] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isDownloadingInfo, setIsDownloadingInfo] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [noPhiCheck, setNoPhiCheck] = useState(
    Boolean(ivrProductData?.NoPHICheck)
  );

  const normalizeStatusKey = (value) => {
    if (!value) return "";
    const raw = String(value).trim().toLowerCase();
    if (!raw) return "";
    const compact = raw.replace(/[^a-z]/g, "");
    switch (compact) {
      case "pending":
        return "pending";
      case "submitted":
        return "submitted";
      case "covered":
        return "covered";
      case "inforequired":
        return "infoRequired";
      case "notcovered":
        return "notCovered";
      default:
        return raw;
    }
  };

  const isCoveredStatus = normalizeStatusKey(activeTab?.key) === "covered";
  const hasReportFile =
    Array.isArray(uploadedInfoFiles) && uploadedInfoFiles.length > 0;
  const isUpdateDisabled =
    (isCoveredStatus && !hasReportFile) || (hasReportFile && !noPhiCheck);

  const STATUS_DISPLAY_MAP = {
    pending: "Pending",
    covered: "Covered",
    submitted: "Submitted",
    infoRequired: "Info Required",
    notCovered: "Not Covered",
  };

  const getStatusDisplay = (value) => {
    const key = normalizeStatusKey(value);
    return STATUS_DISPLAY_MAP[key] ?? value ?? "";
  };

  const getStatusForSave = (value) => {
    const key = normalizeStatusKey(value);
    return STATUS_DISPLAY_MAP[key] ?? value ?? "";
  };

  const STATUS_TAB_TEXT = {
    pending: "Pending",
    submitted: "Submitted",
    covered: "Covered",
    infoRequired: "Info Required",
    notCovered: "Not Covered",
  };

  const getLatestStatusChangedAt = (log, statusValue) => {
    if (!Array.isArray(log) || log.length === 0) return null;
    const targetStatus = normalizeStatusKey(statusValue);
    if (!targetStatus) return null;
    let latest = null;
    for (const entry of log) {
      const entryStatus = normalizeStatusKey(entry?.StatusChangedTo);
      if (entryStatus !== targetStatus) continue;
      const dateValue = entry?.ChangedAt ? new Date(entry.ChangedAt) : null;
      if (!dateValue || Number.isNaN(dateValue.getTime())) continue;
      if (!latest || dateValue > latest) {
        latest = dateValue;
      }
    }
    return latest;
  };

  // checkboxes
  const [notifyUsersByTab, setNotifyUsersByTab] = useState({
    pending: false,
    submitted: false,
    covered: false,
    infoRequired: true,
    notCovered: false,
  });
  const [isFileUpdated, setIsFileUpdated] = useState(false);
  // GetIVRProductRelatedData

  const [ivrData, setIVRData] = useState(null);
  const [ivrAccount, setIvrAccount] = useState(null);
  const [ivrBillingAccount, setIvrBillingAccount] = useState(null);
  const [ivrParentAccount, setIvrParentAccount] = useState(null);
  const [associatedOrders, setAssociatedOrders] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [isRelatedDataLoading, setIsRelatedDataLoading] = useState(true);

  const normalizeInfoFieldType = (field) => {
    const raw =
      field?.FieldType?.value ??
      field?.FieldType?.FieldType ??
      field?.FieldType ??
      field?.type ??
      "";
    return String(raw).trim().toLowerCase();
  };

  const cloneInfoFields = (fields) => {
    if (!Array.isArray(fields)) return [];
    return fields.map((field) => ({
      ...field,
      FieldType: field?.FieldType ? { ...field.FieldType } : field?.FieldType,
      uploadedFiles: Array.isArray(field?.uploadedFiles)
        ? [...field.uploadedFiles]
        : [],
    }));
  };

  const hasInfoFieldValue = (field) => {
    const fieldType = normalizeInfoFieldType(field);
    if (fieldType === "upload-field") {
      const hasNewFile = Array.isArray(field?.uploadedFiles)
        ? field.uploadedFiles.some(
          (file) => file instanceof File || Boolean(file?.blobName)
        )
        : false;
      const hasExistingFile =
        !field?.clearFile &&
        (Boolean(field?.FieldValueAzureBlobName) ||
          Boolean(field?.FieldValueFileName) ||
          Boolean(field?.FieldValueURL));
      return hasNewFile || hasExistingFile;
    }

    return String(field?.FieldValueText ?? "").trim().length > 0;
  };

  const getIncompleteUploadInfoFields = (fields) => {
    if (!Array.isArray(fields) || fields.length === 0) return [];
    return fields.filter((field) => !hasInfoFieldValue(field));
  };

  const incompleteUploadInfoFields = useMemo(
    () => getIncompleteUploadInfoFields(uploadInfoFields),
    [uploadInfoFields]
  );

  const hasCompletedUploadedInfo = useMemo(() => {
    if (!Array.isArray(infoFields) || infoFields.length === 0) {
      return false;
    }
    return infoFields.every((field) => hasInfoFieldValue(field));
  }, [infoFields]);

  const shouldNotifyUsers = Boolean(notifyUsersByTab?.[activeTab?.key]);

  const getHomePath = (currentUser) => {
    const userType = currentUser?.Type;
    if (userType === "Account" && currentUser?.AccountID) {
      return `/Account/${currentUser.AccountID}`;
    }
    if (userType === "Distributor" && currentUser?.DistributorID) {
      return `/Distributor/${currentUser.DistributorID}`;
    }
    if (userType === "Manufacturer" && currentUser?.ManufacturerID) {
      return `/Manufacturer/${currentUser.ManufacturerID}`;
    }
    return "/";
  };

  const redirectToHome = () => {
    const path = getHomePath(user);
    if (!path) return;
    if (window.location.pathname !== path) {
      navigate(path, { replace: true });
    }
  };

  const normalizeOrderStatus = (status) => {
    const normalized = String(status || "").trim().toLowerCase();
    return normalized || "pending";
  };

  const buildInfoFieldsForDownload = () => {
    if (Array.isArray(uploadInfoFields) && uploadInfoFields.length > 0) {
      return uploadInfoFields;
    }
    return Array.isArray(infoFields) ? infoFields : [];
  };

   const hasDownloadableInfo = useMemo(() => {
    const fields = buildInfoFieldsForDownload();
    return fields.some((field) => hasInfoFieldValue(field));
  }, [uploadInfoFields, infoFields]);

  const getIVRProductRelatedData = async () => {
    setIsRelatedDataLoading(true);
    try {
      const serv = new APIService();
      const response = await serv.getDataFromBackend(
        `/ivr/GetIVRProductRelatedData?IVRProductID=${id}`
      );
      const data = response?.data || {};
      console.log("IVR Product Related Data: ", response);
      setIvrProduct(data?.ivrProduct || null);
      setIVRData(data?.ivr || {});
      setIvrAccount(data?.ivrAccount || {});
      setIvrBillingAccount(data?.ivrBillingAccount || {});
      setIvrParentAccount(data?.ivrParentAccount || {});

      const orders = Array.isArray(data?.associatedOrders)
        ? data.associatedOrders.map((order) => ({
          ...order,
          Status: normalizeOrderStatus(order?.Status),
        }))
        : [];
      setAssociatedOrders(orders);

      const activity = Array.isArray(data?.activityLog)
        ? data.activityLog
        : [];
      setActivityLog(activity);

      if (Array.isArray(data?.infoFields) && data.infoFields.length > 0) {
        const normalized = data.infoFields.map((field) => ({
          ID: field?.ID,
          FieldType:
            String(field?.FieldType).trim().toLowerCase() === "upload-field"
              ? { label: "Upload Field", value: "upload-field" }
              : { label: "Text Field", value: "text-field" },
          FieldName: field?.FieldName ?? "",
          FieldValueText: field?.FieldValueText ?? "",
          FieldValueFileName: field?.FieldValueFileName ?? "",
          FieldValueAzureBlobName: field?.FieldValueAzureBlobName ?? "",
          FieldValueURL: field?.FieldValueURL ?? "",
          uploadedFiles: field?.FieldValueAzureBlobName
            ? [
              {
                name: field?.FieldValueFileName || "File",
                blobName: field?.FieldValueAzureBlobName,
              },
            ]
            : [],
          clearFile: false,
        }));
        const clonedFields = cloneInfoFields(normalized);
        setInfoFields(clonedFields);
        setUploadInfoFields(cloneInfoFields(clonedFields));
      } else {
        setInfoFields([]);
        setUploadInfoFields([]);
      }

      if (data?.ivrProduct?.ReportFileAzureBlobName) {
        setUploadedInfoFiles([
          {
            name: data?.ivrProduct?.ReportFileName || "Report",
            blobName: data?.ivrProduct?.ReportFileAzureBlobName ?? "",
          },
        ]);
      } else {
        setUploadedInfoFiles([]);
      }
      setNoPhiCheck(Boolean(data?.ivrProduct?.NoPHICheck));
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        redirectToHome();
        return;
      }
      console.error("Failed to load IVR product related data:", error);
      message.error("Failed to load related IVR data.");
      setAssociatedOrders([]);
      setActivityLog([]);
      setUploadedInfoFiles([]);
      setNoPhiCheck(false);
    } finally {
      setIsRelatedDataLoading(false);
    }
  };

  useEffect(() => {
    getIVRProductRelatedData();
  }, []);

  useEffect(() => {
    if (!ivrProduct) return;
    setLongTextValue(ivrProduct?.Note || "");
    setProductCaseID(ivrProduct?.CaseID || "");
    setNoPhiCheck(Boolean(ivrProduct?.NoPHICheck));
  }, [ivrProduct]);

  useEffect(() => {
    if (!openUpdateStatus) return;
    const statusKey = normalizeStatusKey(ivrProductData?.InsuranceStatus);
    if (!statusKey) return;
    const tabText = STATUS_TAB_TEXT[statusKey];
    if (!tabText) return;
    setActiveTab({ key: statusKey, text: tabText });
    setLongTextValue(ivrProductData?.Note || "");
    setProductCaseID(ivrProductData?.CaseID || "");
    setNoPhiCheck(Boolean(ivrProductData?.NoPHICheck));
    setIsFileUpdated(false);
    if (ivrProductData?.ReportFileAzureBlobName) {
      setUploadedInfoFiles([
        {
          name: ivrProductData?.ReportFileName || "Report",
          blobName: ivrProductData?.ReportFileAzureBlobName ?? "",
        },
      ]);
    } else {
      setUploadedInfoFiles([]);
    }
  }, [openUpdateStatus, ivrProductData]);

  const resetUpdateStatusModalState = () => {
    const nextStatusKey = normalizeStatusKey(ivrProductData?.InsuranceStatus) || "pending";
    setActiveTab({
      key: nextStatusKey,
      text: STATUS_TAB_TEXT[nextStatusKey] || "Pending",
    });
    setLongTextValue(ivrProductData?.Note || "");
    setProductCaseID(ivrProductData?.CaseID || "");
    setUploadedInfoFiles([]);
    setIsFileUpdated(false);
  };

  const handleCloseUpdateStatusModal = () => {
    if (isUpdateSubmitting) return;
    setOpenUpdateStatus(false);
    resetUpdateStatusModalState();
  };


  // useEffect(() => {
  //   let date;
  //   if (ApplicationDate) {
  //     date = getDateFormat(ApplicationDate);
  //   } else {
  //     date = getDateFormat(); // else today's date
  //   }
  //   // const formattedPatientID = ivrData?.IVRASID?.slice(0, -2);
  //   const formattedPatientID = PatientID?.slice(0, -2);
  //   setPONumber(
  //     `PO${date}${formattedPatientID}#${AssociatedOrders}(${ApplicationNumber})`
  //   );
  // }, [location]);

  const handleOpenUploadInfo = () => {
    if (openUploadInfo) {
      setUploadInfoFields(cloneInfoFields(infoFields));
      setOpenUploadInfo(false);
      return;
    }

    setUploadInfoFields(cloneInfoFields(infoFields));
    setOpenUploadInfo(true);
  };

  const handleOpenQuickOrder = () => {
    setOpenQuickOrder(!openQuickOrder);
  };

  const INSURANCE_ICON_CONFIG = {
    covered: {
      className: "success-circle-container",
      icon: <TwoCheckIcon className="icon-container-white" />,
    },
    notCovered: {
      className: "failure-circle-container",
      icon: <DashIcon className="icon-container-white" />,
    },
    infoRequired: {
      className: "warning-circle-container",
      icon: <VerticalDashIcon className="icon-container-white" />,
    },
  };

  const handleDownloadInfo = async () => {
    if (isDownloadingInfo) return;
    if (!ivrData || !ivrProductData) {
      message.error("IVR product data is not available.");
      return;
    }

    setIsDownloadingInfo(true);
    setShowLoader(true);
    try {
      const infoFieldsForDownload = buildInfoFieldsForDownload();
      const pdfBlob = await createIVRInfoPdfBlob({
        ivrData,
        ivrProduct: ivrProductData,
        infoFields: infoFieldsForDownload,
      });

      const zip = new JSZip();
      const pdfName = `IVR_Info_${ivrData?.IVRASID || ivrProductData?.ID || id}.pdf`;
      zip.file(pdfName, pdfBlob);

      const apiServe = new APIService();
      const addBlobIfExists = async (blobName, fileName) => {
        if (!blobName) return;
        try {
          const sasUrl = await apiServe.getSasToken(blobName);
          if (!sasUrl) return;
          const exists = await apiServe.checkBlobExists(sasUrl);
          if (!exists) return;
          const bufferFile = await apiServe.fetchFileAsArrayBuffer(sasUrl);
          zip.file(fileName || "InfoFile.pdf", bufferFile);
        } catch (error) {
          console.warn("Failed to fetch info field file:", error);
        }
      };

      for (const field of infoFieldsForDownload) {
        const fieldType = String(
          field?.FieldType?.value ?? field?.FieldType ?? ""
        ).toLowerCase();
        if (fieldType !== "upload-field") continue;
        const blobName =
          field?.FieldValueAzureBlobName ??
          field?.azureBlobName ??
          null;
        const fileName =
          field?.FieldValueFileName ??
          field?.fileName ??
          null;
        if (blobName) {
          await addBlobIfExists(blobName, fileName || "InfoFile.pdf");
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipName = `IVR_Info_${ivrData?.IVRASID || ivrProductData?.ID || id}.zip`;
      saveAs(zipBlob, zipName);
    } catch (error) {
      console.error("Failed to download info files:", error);
      message.error("Failed to download info files.");
    } finally {
      setIsDownloadingInfo(false);
      setShowLoader(false);
    }
  };

  const handleDownloadReport = async () => {
    if (isDownloadingReport) return;

    const blobName =
      ivrProductData?.ReportFileAzureBlobName ??
      uploadedInfoFiles?.[0]?.blobName ??
      "";
    const fileName =
      ivrProductData?.ReportFileName ??
      uploadedInfoFiles?.[0]?.name ??
      "IVRProductReport.pdf";

    if (!blobName) {
      message.info("No file uplaoded");
      return;
    }

    setIsDownloadingReport(true);
    setShowLoader(true);
    try {
      const apiServe = new APIService();
      const sasUrl = await apiServe.getSasToken(blobName);
      if (!sasUrl) {
        message.info("No file uplaoded");
        return;
      }
      const exists = await apiServe.checkBlobExists(sasUrl);
      if (!exists) {
        message.info("No file uplaoded");
        return;
      }

      const bufferFile = await apiServe.fetchFileAsArrayBuffer(sasUrl);
      const downloadedFileBlob = new Blob([bufferFile], {
        type: "application/octet-stream",
      });
      saveAs(downloadedFileBlob, fileName);
    } catch (error) {
      console.error("Failed to download report file:", error);
      message.error("Failed to download report.");
    } finally {
      setIsDownloadingReport(false);
      setShowLoader(false);
    }
  };

  const INSURANCE_BUTTON_CONFIG = {
    covered: [
      { text: "Download Report", variant: "primary", onClick: handleDownloadReport },
      {
        text: "Quick Order",
        variant: "success",
        onClick: handleOpenQuickOrder,
      },
    ],
    notCovered: [{ text: "Download Report", variant: "primary", onClick: handleDownloadReport }],
    infoRequired: [
      ...(hasCompletedUploadedInfo
        ? [
          {
            text: "Info Uploaded",
            isStaticText: true,
          },
        ]
        : [
          {
            text: "Upload Info",
            variant: "warning",
            onClick: handleOpenUploadInfo,
          },
        ]),
      ...(user?.Type === "Admin" && hasDownloadableInfo
        ? [
          {
            text: "Download Info",
            variant: "failure",
            onClick: handleDownloadInfo,
          },
        ]
        : []),
    ],
  };

  const QUICK_ORDER_MODAL_CONFIG = {
    covered: [
      {
        fieldTitle: "Patient ID",
        fieldValue: ivrData?.IVRASID,
        fieldColor: "successColorDark",
      },
      {
        fieldTitle: "CaseID",
        fieldValue: ivrProductData?.CaseID,
        fieldColor: "successColorDark",
      },
      {
        fieldTitle: "Product",
        fieldValue: ivrProductData?.ProductName,
        fieldColor: "successColorDark",
      },
    ],
  };

  const normalizedInsuranceStatus = normalizeStatusKey(
    ivrProductData?.InsuranceStatus
  );
  const insuranceStatusText = getStatusDisplay(
    ivrProductData?.InsuranceStatus
  );

  const latestStatusChangedAt = useMemo(() => {
    return getLatestStatusChangedAt(activityLog, ivrProductData?.InsuranceStatus);
  }, [activityLog, ivrProductData?.InsuranceStatus]);

  const latestStatusChangedOnText = latestStatusChangedAt
    ? moment(latestStatusChangedAt).format("MM-DD-YYYY")
    : ivrProductData?.updatedAt
      ? moment(ivrProductData.updatedAt).format("MM-DD-YYYY")
      : "N/A";

  const renderInsuranceStatusModalComponent =
    QUICK_ORDER_MODAL_CONFIG[normalizedInsuranceStatus]?.map(
      ({ fieldTitle, fieldValue, fieldColor }) => (
        <div className="quick-order-modal-field-component">
          <Text variant="bodyText">{fieldTitle}</Text>
          <Text variant="h1" color={fieldColor}>
            {fieldValue}
          </Text>
        </div>
      )
    ) || null;

  const RENDER_RIGHT_COMPONENT_OBJECT = {
    rightHeadingIcon: <CalenderIcon className="icon-container" />,
    rightHeadingText: "Last Updated On",
    rightHeadingValue: latestStatusChangedOnText,
  };

  const renderIconObject = INSURANCE_ICON_CONFIG[normalizedInsuranceStatus] || null;
  const renderIcon = renderIconObject ? (
    <div className={renderIconObject.className}>{renderIconObject.icon}</div>
  ) : null;

  const renderButtonComponent =
    INSURANCE_BUTTON_CONFIG[normalizedInsuranceStatus]?.map(
      ({ text, variant, onClick, isStaticText }) =>
        isStaticText ? (
          <Text key={text} variant="bodyText">
            {text}
          </Text>
        ) : (
          <Button
            key={text}
            variant="table"
            subVariant={variant}
            onClick={onClick}
          >
            {text}
          </Button>
        )
    ) || null;

  const renderRightComponent = RENDER_RIGHT_COMPONENT_OBJECT;

  const RENDER_INSURANCE_TABS = [
    {
      key: "pending",
      text: "Pending",
      onClick: () => setActiveTab({ key: "pending", text: "Pending" }),
    },
    {
      key: "submitted",
      text: "Submitted",
      onClick: () => setActiveTab({ key: "submitted", text: "Submitted" }),
    },
    {
      key: "covered",
      text: "Covered",
      onClick: () => setActiveTab({ key: "covered", text: "Covered" }),
    },
    {
      key: "infoRequired",
      text: "Info Required",
      onClick: () =>
        setActiveTab({ key: "infoRequired", text: "Info Required" }),
    },
    {
      key: "notCovered",
      text: "Not Covered",
      onClick: () => setActiveTab({ key: "notCovered", text: "Not Covered" }),
    },
  ];

  const addField = () => {
    setInfoFields((prev) => [
      ...prev,
      {
        FieldType: {
          label: "Text Field",
          value: "text-field",
        },
        FieldName: "",
      },
    ]);
  };

  const handleChangeEdit = (index, field, value) => {
    setInfoFields((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleReportFilesChange = (files) => {
    const nextFiles = Array.isArray(files) ? files : [];
    setIsFileUpdated(true);
    setUploadedInfoFiles(nextFiles);
    setNoPhiCheck(false);
  };

  const handleProductUpdate = async () => {
    if (isCoveredStatus && !hasReportFile) {
      message.error("A report file is required when status is Covered.");
      return;
    }
    if (hasReportFile && !noPhiCheck) {
      message.error("PHI certification is required when a report file exists.");
      return;
    }
    if (isUpdateSubmitting) return;

    const serv = new APIService();
    setIsUpdateSubmitting(true);
    const infoFieldsPayload =
      activeTab?.key === "infoRequired"
        ? (infoFields || [])
          .map((field) => {
            const fieldTypeValue =
              field?.FieldType?.value ??
              field?.FieldType?.FieldType ??
              field?.FieldType ??
              field?.type ??
              "";
            const fieldName = String(field?.FieldName ?? "").trim();
            if (!fieldName) return null;
            return {
              ID: field?.ID ?? field?.id ?? null,
              FieldType: fieldTypeValue,
              FieldName: fieldName,
              FieldValueText: field?.FieldValueText ?? "",
              FieldValueFileName: field?.FieldValueFileName ?? "",
              FieldValueAzureBlobName: field?.FieldValueAzureBlobName ?? "",
              FieldValueURL: field?.FieldValueURL ?? "",
            };
          })
          .filter(Boolean)
        : undefined;

    const postObj = {
      productGroupId: ivrProductData?.ID ?? ivrProductData?.ProductGroupID,
      caseId: productCaseID,
      note: longTextValue,
      status: getStatusForSave(activeTab?.key),
      notifyUsers: shouldNotifyUsers,
      noPhiCheck: hasReportFile ? noPhiCheck : false,
      isFileUpdated: isFileUpdated,
      ivrId: ivrData?.ID,
      ...(activeTab?.key === "infoRequired" ? { infoFields: infoFieldsPayload } : {})
    }
    const formData = new FormData();

    if (Array.isArray(uploadedInfoFiles) && uploadedInfoFiles.length > 0 && isFileUpdated) {
      uploadedInfoFiles.forEach((file) => {
        formData.append("reportFiles", file);
      });
    }
    formData.append("data", JSON.stringify(postObj));

    try {
      const response = await serv.addFileDataToBackend(
        formData,
        "/ivr/updateivrproduct",
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Update IVR Product Response: ", response);
      if (response?.status === 200 || response?.status === 201) {
        setOpenUpdateStatus(false);
        resetUpdateStatusModalState();
        await getIVRProductRelatedData();
        message.success("Product updated successfully.");
        return;
      }
      message.error("Failed to update product. Please try again.");
    } catch (error) {
      console.error("Update IVR product failed:", error);
      message.error(
        error?.response?.data?.message || "Failed to update product. Please try again."
      );
      setOpenUpdateStatus(false);
    } finally {
      setIsUpdateSubmitting(false);
    }
  }

  const handleInfoFieldTextChange = (index, value) => {
    setUploadInfoFields((prev) =>
      prev.map((field, i) =>
        i === index
          ? {
            ...field,
            FieldValueText: value,
          }
          : field
      )
    );
  };

  const handleInfoFieldFilesChange = (index, files) => {
    const nextFiles = Array.isArray(files) ? files : [];
    setUploadInfoFields((prev) =>
      prev.map((field, i) => {
        if (i !== index) return field;
        const hasExisting =
          Boolean(field?.FieldValueAzureBlobName) ||
          Boolean(field?.FieldValueFileName);
        const clearFile = hasExisting && nextFiles.length === 0;
        return {
          ...field,
          uploadedFiles: nextFiles,
          clearFile,
        };
      })
    );
  };

  const handleUploadInfoSubmit = async () => {
    if (isUploadingInfo) return;
    if (!Array.isArray(uploadInfoFields) || uploadInfoFields.length === 0) {
      message.error("No info fields are configured for this IVR product.");
      return;
    }
    if (incompleteUploadInfoFields.length > 0) {
      const hasIncompleteUploadFields = incompleteUploadInfoFields.some(
        (field) => normalizeInfoFieldType(field) === "upload-field"
      );
      const hasIncompleteTextFields = incompleteUploadInfoFields.some(
        (field) => normalizeInfoFieldType(field) !== "upload-field"
      );

      if (hasIncompleteUploadFields && hasIncompleteTextFields) {
        message.error("Please complete all required text fields and upload all required files.");
      } else if (hasIncompleteUploadFields) {
        message.error("Please upload files for all upload fields.");
      } else {
        message.error("Please complete all required text fields.");
      }
      return;
    }

    const formData = new FormData();
    const infoFieldsPayload = uploadInfoFields
      .map((field, index) => {
        const fieldId = field?.ID ?? field?.id ?? null;
        if (!fieldId) return null;

        const fieldTypeValue =
          field?.FieldType?.value ??
          field?.FieldType?.FieldType ??
          field?.FieldType ??
          field?.type ??
          "";

        const fileEntry = Array.isArray(field?.uploadedFiles)
          ? field.uploadedFiles.find((f) => f instanceof File)
          : null;
        const fileKey = fileEntry ? `infoFieldFile_${fieldId}` : null;
        if (fileEntry && fileKey) {
          formData.append(fileKey, fileEntry);
        }

        return {
          ID: fieldId,
          FieldType: fieldTypeValue,
          FieldName: field?.FieldName ?? "",
          FieldValueText: field?.FieldValueText ?? "",
          fileKey,
          clearFile: Boolean(field?.clearFile),
          SortOrder: Number.isFinite(Number(field?.SortOrder))
            ? Number(field.SortOrder)
            : index,
        };
      })
      .filter(Boolean);

    formData.append(
      "data",
      JSON.stringify({
        ivrProductId: ivrProductData?.ID ?? id,
        infoFields: infoFieldsPayload,
      })
    );

    setIsUploadingInfo(true);
    setShowLoader(true);
    try {
      const serv = new APIService();
      const response = await serv.addFileDataToBackend(
        formData,
        "/ivr/updateivrproductinfofields",
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response?.status === 200) {
        message.success("Info uploaded successfully.");
        await getIVRProductRelatedData();
        return;
      } else {
        message.error("Failed to upload info. Please try again.");
      }
    } catch (error) {
      console.error("Upload info failed:", error);
      message.error("Failed to upload info. Please try again.");
    } finally {
      setIsUploadingInfo(false);
      setShowLoader(false);
      setOpenUploadInfo(false);
    }
  };

  const deleteIVR = async () => {
    if (!ivrProductData || !canDeleteProduct) return;
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone. The IVR Product will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      // console.log("Delete confirmation result: ", result);
      if (result.isConfirmed) {

        setShowLoader(true);
        try {
          const serv = new APIService();
          const response = await serv.addDataToBackend({ivrProductId: ivrProductData.ID}, "/ivr/deletenewivrproduct");
          if (response?.status === 200) {
            message.success("IVR product deleted successfully.");
            // redirectToHome();
            // return;
            window.location.href = "/IVRDetails/" + ivrProductData.IVRID; 
          }
          message.error("Failed to delete IVR product. Please try again.");
        } catch (error) {
          console.error("Delete IVR product failed:", error);
          message.error("Failed to delete IVR product. Please try again.");
        } finally {
          setShowLoader(false);
        }
      }
    });
  }

  const canEditProduct = canEditIvrProduct(user);
  const canDeleteProduct = canDeleteIvrProduct(user);
  const canShowProductActions = canEditProduct || canDeleteProduct;

  return (
    <>
      {showLoader && (
        <Spin fullscreen indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      )}
      {ivrData && ivrProductData && ivrAccount &&
        <div className="global-parent-container-wrapper">
          <TitleBar
            entityName={ivrProductData?.IVRID ? `IVR ID ${ivrProductData?.IVRID}` : undefined}
            label={PatientID ? `Patient ID ${PatientID}` : undefined}
            // label={`${ivrProductData?.ProductGroupName}`}
            invoiceNumber={ivrProductData?.ProductGroupName ? `Product ${ivrProductData.ProductGroupName}` : undefined}
          />
          {/* <button onClick={()=>setOpenQuickOrder(true)}>Quick Order</button> */}
          {canShowProductActions &&
            <AdminActions
              secondButtonText={canEditProduct ? "Update Status" : undefined}
              secondButtonType={canEditProduct ? "warning" : undefined}
              secondButtonFn={canEditProduct ? () => setOpenUpdateStatus(true) : undefined}
              fourthButtonText="Delete"
              fourthButtonType="failure"
              fourthButtonFn={deleteIVR}
              fourthButtonCondition={canDeleteProduct}
              IVRPath={true}
            />
          }
          <InsuranceStatusPanel
            insuranceStatus={normalizedInsuranceStatus}
            // insuranceStatusText={InsuranceStatusText}
            insuranceStatusText={insuranceStatusText}
            // insuranceStatusText={"Submitted"}
            leftHeadingIcon={renderIcon}
            buttonComponent={renderButtonComponent}
            {...renderRightComponent}
          />
          <DetailsContainer
            firstHeading="Ship To Account"
            // accountName="Foot & Ankle Specialists of Central PA-Ship"
            accountName={ivrData?.AccountName}
            accountID={ivrData?.AccountID}
            // isChild={true}
            isChild={ivrAccount?.IsChildAccount}
            // childAccount="Child Account of Foot & Ankle Specialists"
            childAccount={ivrAccount?.IsChildAccount ? `Child Account of ${ivrParentAccount?.AccountName}` : null}
            secondHeading="Bill To Account"
            secondIcon={<AccountsIcon className="icon-container" />}
            // distributorName="Foot & Ankle Specialists of Central PA-Bill"
            distributorName={ivrAccount?.BillingAccountName}
            distributorID={ivrAccount?.BillingAccountID}
            accountPath="Account"
            thirdHeading="Distributor"
            thirdIcon={<DistributorIcon className="icon-container" />}
            // manufacturerName="House"
            manufacturerName={ivrData?.DistributorName}
            manufacturerID={ivrData?.DistributorID}
            distributorPath="Distributor"
            fourthHeading="Manufacturer"
            fourthIcon={<ManufacturerIcon className="icon-container" />}
            // fourthValue="BioLab Sciences"
            fourthValue={ivrProductData?.ManufacturerName}
            fourthPath={ivrProductData?.ManufacturerID ? `/Manufacturer/${ivrProductData.ManufacturerID}` : undefined}
            // rightFirstHeading="Request Date"
            // invoiceDate="January 1, 2024"
            // rightSecondHeading="Last Updated"
            // invoiceDue="February 1, 2024"
            showRightFirstIcon={false}
            showRightSecondIcon={false}
          />
          <div className="ivr-product-container">
            <div className="ivr-product-order-container">
              <Text variant="bodyText">Associated Orders</Text>
              <Table
                rowData={associatedOrders}
                columnDefs={ivrProductColDefs}
                loading={isRelatedDataLoading}
              />
            </div>
            <div className="ivr-product-activity-container">
              <Text variant="bodyText">Associated Activity</Text>
              <div className="ivr-product-activity-child-container">
                {activityLog?.length === 0 && !isRelatedDataLoading && (
                  <Text variant="smallText" color="lightText">
                    No activity recorded yet.
                  </Text>
                )}
                {activityLog?.map((activity) => {
                  const caseIdText = ivrProductData?.CaseID || "IVR Product";
                  const fromStatus = activity?.StatusChangedFrom;
                  const toStatus = activity?.StatusChangedTo;
                  const statusText =
                    fromStatus && toStatus
                      ? `${caseIdText} status was changed from ${fromStatus} to ${toStatus}`
                      : toStatus
                        ? `${caseIdText} status was set to ${toStatus}`
                        : `${caseIdText} status was updated`;

                  const nameParts = [activity?.FirstName, activity?.LastName]
                    .filter(Boolean)
                    .join(" ")
                    .trim();
                  const actorLabel =
                    nameParts || activity?.Email || "System";
                  const userTypeLabel = activity?.UserType
                    ? ` (${activity.UserType})`
                    : "";

                  return (
                    <div
                      key={activity?.ID || activity?.ChangedAt}
                      className="ivr-product-activity-stepper-container"
                    >
                      <Text variant="smallText">{statusText}</Text>
                      <div className="ivr-product-activity-stepper-inner-container">
                        <Text variant="tinyText" color="lightText">
                          {activity?.ChangedAt
                            ? moment(activity.ChangedAt).format("MM-DD-YYYY")
                            : ""}
                        </Text>
                        <Text variant="tinyText" color="lightText">
                          By {actorLabel}
                          {userTypeLabel}
                        </Text>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      }
      {openUploadInfo && (
        <ModalView
          isOpen={openUploadInfo}
          onClose={handleOpenUploadInfo}
          modalTitle="Upload Info"
          textInputComponent={
            <div className="ivr-16-gap-modal-container">
              <div className="field-group-wrapper">
                <div className="field-group" style={{ alignItems: "center" }}>
                  <DropdownComponent
                    label="Product Name"
                    placeholder="Select Product"
                    isRequired
                    isDisabled
                    options={[]}
                    value={{
                      label: ivrProductData?.ProductGroupName || "",
                      value:
                        ivrProductData?.ProductGroupID ??
                        ivrProductData?.ID ??
                        "",
                    }}
                  />
                  {/* once there is a condition for isDisabled prop we will add the same condition to alter the design for the disabled button */}
                  <TextInput
                    titleText="Case ID"
                    placeholder="Enter Case ID"
                    isDisabled
                    value={productCaseID || ivrProductData?.CaseID || ""}
                  />
                </div>
              </div>
              {Array.isArray(infoFields) && infoFields.length > 0 ? (
                uploadInfoFields.map((field, index) => (
                  <div className="field-group-wrapper" key={field?.ID ?? index}>
                    <div className="field-group" style={{ alignItems: "center" }}>
                      {field?.FieldType?.value === "upload-field" ? (
                        <div className={styles.fileUploadSection}>
                          <FileUpload
                            label={field?.FieldName || "Upload File"}
                            uploadedFiles={field?.uploadedFiles || []}
                            setUploadedFiles={(files) =>
                              handleInfoFieldFilesChange(index, files)
                            }
                          />
                        </div>
                      ) : (
                        <TextInput
                          titleText={field?.FieldName || "Field Value"}
                          placeholder="Enter value"
                          parentClassName="full-width-button"
                          value={field?.FieldValueText || ""}
                          setValue={(val) =>
                            handleInfoFieldTextChange(index, val)
                          }
                        />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <Text variant="smallText" color="lightText">
                  No info fields are configured for this IVR product.
                </Text>
              )}
            </div>
          }
          footerButtonWrapper={
            <div
              className="footer-button-wrapper-flex-end"
              style={{ gap: "12px" }}
            >
              <Button
                variant="table"
                subVariant=""
                onClick={handleOpenUploadInfo}
              >
                Cancel
              </Button>
              <Button
                variant="table"
                subVariant="primary"
                onClick={handleUploadInfoSubmit}
                disabled={
                  isUploadingInfo ||
                  (uploadInfoFields?.length ?? 0) === 0 ||
                  incompleteUploadInfoFields.length > 0
                }
              >
                {isUploadingInfo ? "Uploading..." : "Upload"}
              </Button>
            </div>
          }
        />
      )}
      {openUpdateStatus && (
        <ModalView
          isOpen={openUpdateStatus}
          onClose={handleCloseUpdateStatusModal}
          modalTitle="Update Status"
          textInputComponent={
            <div className="ivr-16-gap-modal-container">
              <div className="modal-warning-container">
                <FailureIcon
                  className="warning-icon-container"
                  width={16}
                  height={16}
                />
                <Text>
                  This information is accessible to all users with access to this IVR. DO NOT upload or request any PHI in this section.
                </Text>
              </div>
              <InsuranceStatusSelector
                activeTab={activeTab}
                buttons={RENDER_INSURANCE_TABS}
              />
              <div className="field-group-wrapper">
                <div className="field-group" style={{ alignItems: "center" }}>
                  <DropdownComponent
                    label="Product Name"
                    placeholder="Select Product"
                    isRequired
                    // isDisabled={activeTab === "Info Required"}
                    isDisabled={true}
                    value={{ label: ivrProductData.ProductGroupName, value: ivrProductData.ProductGroupID }}
                  // options={[
                  //   { label: "Product#1", value: "distributor" },
                  //   {
                  //     label: "Product#2",
                  //     value: "advanced-solution",
                  //   },
                  // ]}
                  />
                  <TextInput
                    titleText="Case ID"
                    placeholder="Enter Case ID"
                    value={productCaseID}
                    setValue={setProductCaseID}
                  // isDisabled={true}
                  />
                </div>
              </div>
              <TextAreaComponent
                placeholder="Add a note"
                value={longTextValue}
                setValue={setLongTextValue}
              />
              {activeTab?.key !== "infoRequired" ? (
                <FileUpload
                  // label="Upload {File Name}"
                  label={uploadedInfoFiles?.length > 0 ? "File" : "Upload File"}
                  uploadedFiles={uploadedInfoFiles}
                  setUploadedFiles={handleReportFilesChange}
                />
              ) : null}
              {isCoveredStatus && !hasReportFile ? (
                <Text variant="tinyText" color="failureColor">
                  A report file is required when status is Covered.
                </Text>
              ) : null}
              {hasReportFile ? (
                <div style={{ display: "flex", gap: "8px" }}>
                  <Checkbox
                    checked={noPhiCheck}
                    onChange={(event) => {
                      setNoPhiCheck(event?.target?.checked === true);
                    }}
                  />
                  <Text variant="smallText">
                    I certify that this file contains no PHI (Protected Health
                    Information) about the patient{" "}
                    <span style={{ color: "red" }}>*</span>
                    <br />
                    <p className="input-subtitle">
                      {isFileUpdated
                        ? `File will be uploaded by: ${user?.FirstName || ""} ${user?.LastName || ""}`.trim()
                        : ivrProductData?.FileUploadedDate
                          ? `Current file uploaded on: ${moment(
                            ivrProductData.FileUploadedDate
                          ).format("MM-DD-YYYY")}`
                          : "Certification is required for the current report file."}
                    </p>
                  </Text>
                </div>
              ) : null}
              {activeTab?.key === "infoRequired" ? (
                <Button variant="text-link" onClick={() => addField()}>
                  Add User Input Field
                </Button>
              ) : null}
              {activeTab?.key === "infoRequired"
                ? infoFields?.map((field, index) => {
                  return (
                    <div className="field-group-wrapper" key={index}>
                      <div
                        className="field-group"
                        style={{ alignItems: "center" }}
                      >
                        <DropdownComponent
                          label="Field Type"
                          placeholder=""
                          isRequired
                          value={field.FieldType}
                          // setValue={setFieldTypeValue}
                          onChange={(option) =>
                            handleChangeEdit(index, "FieldType", option)
                          }
                          options={[
                            { label: "Text Field", value: "text-field" },
                            {
                              label: "Upload Field",
                              value: "upload-field",
                            },
                          ]}
                        />
                        <TextInput
                          titleText={
                            field.FieldType?.value === "upload-field"
                              ? "Upload File Name"
                              : "File Name"
                          }
                          placeholder={
                            field.FieldType?.value === "upload-field"
                              ? "Enter file name to be uploaded"
                              : "Enter field name"
                          }
                          value={field?.FieldName}
                          onChange={(e) =>
                            handleChangeEdit(
                              index,
                              "FieldName",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })
                : null}

              <div style={{ display: "flex", gap: "8px" }}>
                <Checkbox
                  checked={shouldNotifyUsers}
                  onChange={(event) => {
                    setNotifyUsersByTab((prev) => ({
                      ...prev,
                      [activeTab?.key]: event?.target?.checked === true,
                    }));
                  }}
                />
                <Text variant="smallText">
                  Notify associated users via email
                </Text>
              </div>
            </div>
          }
          footerButtonWrapper={
            <div
              className="footer-button-wrapper-flex-end"
              style={{ gap: "12px" }}
            >
              <Button
                variant="table"
                subVariant=""
                // onClick={() => setOpenUpdateStatus(false)}
                onClick={handleCloseUpdateStatusModal}
                disabled={isUpdateSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="table"
                subVariant="primary"
                onClick={handleProductUpdate}
                disabled={isUpdateDisabled || isUpdateSubmitting}
              >
                {isUpdateSubmitting ? (
                  <>
                    <LoadingOutlined spin style={{ marginRight: 8 }} />
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </Button>
            </div>
          }
        />
      )}
      {openQuickOrder &&
        <QuickOrder
          isOpen={openQuickOrder}
          onClose={() => setOpenQuickOrder(false)}
          ivrData={ivrData}
          ivrProductData={ivrProductData}
          ivrAccount={ivrAccount}
          ivrBillingAccount={ivrBillingAccount}
          ivrParentAccount={ivrParentAccount}
          insuranceStatusPanelProps={{
            insuranceStatus: normalizedInsuranceStatus,
            insuranceStatusText: insuranceStatusText,
            leftHeadingIcon: renderIcon,
            buttonComponent: renderButtonComponent,
            modalVariantComponent: renderInsuranceStatusModalComponent,
            // ...renderRightComponent,
          }}
        />
      }
    </>
  );
};

export default IVRProduct;
